import { createEmptyFaixa } from "../services/freightTableConfigForm";
import {
  areCustosExtraReady,
  createEmptyTaxas,
  findCustoExtraIssues,
  mapCustosExtraToApi,
  mapTaxasToApi,
  sentCustoExtraIndexes,
} from "./validityTaxes";

const ROW_ERROR_KEY = /^veiculos\.(\d+)\.(tp_rodado\.value|tp_rodado|vlr)$/;
const RANGE_ERROR_KEY = /^calculo\.(\d+)\.(vlr_ini|vlr_fim|vlr|fixa|excedente|vlr_exd|fator_conv)$/;
const COMMISSION_ERROR_KEY = /^comissao\.(vlr_taxa|taxa_comissao|vlr_minimo|custo\.value|custo)$/;
const BLOCK_ERROR_KEY = /^vigencias\.(\d+)\.(.+)$/;
const TAX_ERROR_KEY = /^taxas\.([a-z_]+)(?:\.value)?$/;
const COST_ERROR_KEY = /^custo_extra\.(\d+)(?:\.(.+))?$/;
const ROUTE_STATUS_ERROR_KEY = "rota_status";
const COST_FIELD_BY_KEY = {
  "custo.value": "custo_id",
  custo: "custo_id",
  "tp_trecho.value": "tp_trecho",
  tp_trecho: "tp_trecho",
  "tp_trecho_custo.value": "tp_trecho_custo",
  tp_trecho_custo: "tp_trecho_custo",
  vlr_taxa: "vlr_taxa",
  vlr_minimo: "vlr_minimo",
  taxa_comissao: "taxa_comissao",
};
const PERIOD_KEYS = ["dt_vigencia_ini", "dt_vigencia_fim"];

const CALC_FAIXA_KM = 2;
const CALC_COMISSAO = 6;
const CALC_PERCENTUAL_NF = 7;
const TRECHO_COMISSAO = 4;
const STATUS_ATIVO = 1;
const STATUS_CANCELADA = 3;

export const NEW_VALIDITY_MAX_BLOCKS = 20;
export const NEW_VALIDITY_MAX_RANGES = 50;
export const PERCENTUAL_NF_MAX = 100;
export const TAXA_COMISSAO_MAX = 99.999;
export const COMMISSION_TRECHO_MESSAGE =
  "Em tabela do tipo Comissão, o custo de comissão é informado na comissão, não nos custos extras.";

export const NEW_VALIDITY_MODE = {
  VEHICLES: "vehicles",
  RANGES: "ranges",
  VALUE: "value",
  COMMISSION: "commission",
};

const MODE_BY_CALC = {
  1: NEW_VALIDITY_MODE.RANGES,
  2: NEW_VALIDITY_MODE.RANGES,
  3: NEW_VALIDITY_MODE.RANGES,
  4: NEW_VALIDITY_MODE.VALUE,
  5: NEW_VALIDITY_MODE.VEHICLES,
  6: NEW_VALIDITY_MODE.COMMISSION,
  7: NEW_VALIDITY_MODE.VALUE,
  8: NEW_VALIDITY_MODE.RANGES,
};

export function newValidityModeFor(tpCalc) {
  return MODE_BY_CALC[Number(tpCalc)] || null;
}

export function isPercentualCalc(tpCalc) {
  return Number(tpCalc) === CALC_PERCENTUAL_NF;
}

export function createEmptyNewValidityLine() {
  return { vlr: null, tpRodados: [] };
}

export function excludedTrechosFor(tpCalc) {
  return Number(tpCalc) === CALC_COMISSAO ? [TRECHO_COMISSAO] : [];
}

function custoExtraOptions(tpCalc) {
  return {
    excludedTrechos: excludedTrechosFor(tpCalc),
    excludedMessage: COMMISSION_TRECHO_MESSAGE,
  };
}

export function findNewValidityCostIssues(block, tpCalc) {
  const taxas = (block && block.taxas) || {};
  return findCustoExtraIssues(taxas.custo_extra, custoExtraOptions(tpCalc));
}

export function createEmptyCommission() {
  return { vlrTaxa: null, taxaComissao: null, vlrMinimo: null, custoId: null };
}

export function createEmptyNewValidityBlock(defaults) {
  const base = defaults || {};

  return {
    inicio: null,
    fim: null,
    linhas: [createEmptyNewValidityLine()],
    faixas: [createEmptyFaixa()],
    vlr: null,
    comissao: { ...createEmptyCommission(), ...(base.comissao || {}) },
    taxas: createEmptyTaxas(),
  };
}

export function newValidityDefaults(context) {
  const source = context || {};

  return {
    comissao: source.commissionDefaults || null,
    // TMS-8964: TDA/TDE, pedágio, outros e custo não vêm da vigência anterior.
    taxas: null,
  };
}

export function createEmptyNewValidityForm(defaults) {
  return { blocos: [createEmptyNewValidityBlock(defaults)] };
}

function isBlank(value) {
  return value === null || value === undefined || value === "";
}

function isPositive(value) {
  if (isBlank(value)) return false;
  const number = Number(value);
  return Number.isFinite(number) && number > 0;
}

function isNonNegative(value) {
  if (isBlank(value)) return false;
  const number = Number(value);
  return Number.isFinite(number) && number >= 0;
}

function optionalNumber(value) {
  return isBlank(value) ? null : Number(value);
}

function lineVehicles(line) {
  return ((line && line.tpRodados) || [])
    .filter((id) => !isBlank(id))
    .map(Number);
}

function isOn(value) {
  return Number(value) === 1;
}

function dayKey(value) {
  const parts = String(value || "").split("/");
  if (parts.length !== 3) return null;
  return Number(parts[2] + parts[1] + parts[0]);
}

function periodLabel(block) {
  return `${block.inicio} – ${block.fim || "em aberto"}`;
}

export function formatRangeNumber(value) {
  return String(Number(value)).replace(".", ",");
}

function rangeLabel(faixa) {
  return `${formatRangeNumber(faixa.vlr_ini)} – ${formatRangeNumber(faixa.vlr_fim)}`;
}

function rangesOverlap(a, b) {
  const aIni = Number(a.vlr_ini);
  const aFim = Number(a.vlr_fim);
  const bIni = Number(b.vlr_ini);
  const bFim = Number(b.vlr_fim);

  if (aIni === bIni && aFim === bFim) return true;
  if (Math.max(aIni, bIni) < Math.min(aFim, bFim)) return true;
  if (aIni === aFim && bIni < aIni && aIni < bFim) return true;
  if (bIni === bFim && aIni < bIni && bIni < aFim) return true;
  return false;
}

function hasRangeBounds(faixa) {
  return (
    isNonNegative(faixa.vlr_ini) &&
    isPositive(faixa.vlr_fim) &&
    Number(faixa.vlr_fim) >= Number(faixa.vlr_ini)
  );
}

function addRangeIssue(issues, index, field, message) {
  const current = issues[index] || {};
  if (current[field]) return;
  issues[index] = { ...current, [field]: message };
}

export function findRangeIssues(faixas, tpCalc) {
  const list = faixas || [];
  const issues = {};

  list.forEach((faixa, index) => {
    if (
      !isBlank(faixa.vlr_ini) &&
      !isBlank(faixa.vlr_fim) &&
      Number(faixa.vlr_fim) < Number(faixa.vlr_ini)
    ) {
      addRangeIssue(issues, index, "vlr_fim", "A faixa final deve ser maior que a inicial.");
    }

    if (
      Number(tpCalc) === CALC_FAIXA_KM &&
      isOn(faixa.excedente) &&
      !isBlank(faixa.vlr_ini) &&
      Number(faixa.vlr_ini) <= 0
    ) {
      addRangeIssue(issues, index, "vlr_ini", "A faixa de excedente deve começar acima de zero.");
    }
  });

  for (let i = 0; i < list.length; i += 1) {
    for (let j = i + 1; j < list.length; j += 1) {
      const a = list[i];
      const b = list[j];
      if (!hasRangeBounds(a) || !hasRangeBounds(b) || !rangesOverlap(a, b)) continue;

      const aIni = Number(a.vlr_ini);
      const bIni = Number(b.vlr_ini);
      const target = bIni >= aIni ? j : i;
      const other = target === j ? i : j;

      addRangeIssue(
        issues,
        target,
        "vlr_ini",
        `A faixa ${rangeLabel(list[target])} se sobrepõe à faixa ${rangeLabel(list[other])}.`,
      );
    }
  }

  return issues;
}

function isRangeComplete(faixa) {
  if (!hasRangeBounds(faixa) || !isPositive(faixa.vlr)) return false;
  if (isOn(faixa.excedente) && !isPositive(faixa.vlr_exd)) return false;
  return true;
}

function isCommissionComplete(comissao) {
  const value = comissao || {};
  if (!isPositive(value.vlrTaxa) && !isPositive(value.taxaComissao)) return false;
  if (!isBlank(value.taxaComissao) && Number(value.taxaComissao) > TAXA_COMISSAO_MAX) {
    return false;
  }
  return !isBlank(value.custoId);
}

export function isNewValidityBlockComplete(block, tpCalc) {
  if (!block || !block.inicio) return false;

  const taxas = block.taxas || {};
  if (!areCustosExtraReady(taxas.custo_extra, custoExtraOptions(tpCalc))) {
    return false;
  }

  const mode = newValidityModeFor(tpCalc);

  if (mode === NEW_VALIDITY_MODE.VEHICLES) {
    const linhas = block.linhas || [];
    return (
      linhas.length > 0 &&
      linhas.every((line) => lineVehicles(line).length > 0 && isPositive(line.vlr))
    );
  }

  if (mode === NEW_VALIDITY_MODE.RANGES) {
    const faixas = block.faixas || [];
    return (
      faixas.length > 0 &&
      faixas.length <= NEW_VALIDITY_MAX_RANGES &&
      faixas.every(isRangeComplete) &&
      !Object.keys(findRangeIssues(faixas, tpCalc)).length
    );
  }

  if (mode === NEW_VALIDITY_MODE.VALUE) {
    if (!isPositive(block.vlr)) return false;
    return !isPercentualCalc(tpCalc) || Number(block.vlr) <= PERCENTUAL_NF_MAX;
  }

  if (mode === NEW_VALIDITY_MODE.COMMISSION) {
    return isCommissionComplete(block.comissao);
  }

  return false;
}

export function findNewValidityBlockConflicts(form) {
  const blocos = (form && form.blocos) || [];
  const conflicts = {};

  blocos.forEach((block, index) => {
    const start = dayKey(block.inicio);
    if (start === null) return;
    const end = block.fim ? dayKey(block.fim) : Infinity;

    for (let other = 0; other < index; other += 1) {
      const previous = blocos[other];
      const previousStart = dayKey(previous.inicio);
      if (previousStart === null) continue;
      const previousEnd = previous.fim ? dayKey(previous.fim) : Infinity;

      if (start === previousStart) {
        conflicts[index] = `Outra vigência desta inclusão também começa em ${block.inicio}.`;
        return;
      }

      if (start <= previousEnd && previousStart <= end) {
        conflicts[index] = `O período ${periodLabel(
          block,
        )} se sobrepõe a outra vigência desta inclusão (${periodLabel(previous)}).`;
        return;
      }
    }
  });

  return conflicts;
}

export function isNewValidityComplete(form, tpCalc) {
  const blocos = (form && form.blocos) || [];
  if (!blocos.length) return false;

  return (
    blocos.every((block) => isNewValidityBlockComplete(block, tpCalc)) &&
    !Object.keys(findNewValidityBlockConflicts(form)).length
  );
}

export function selectedVehicleIds(linhas, exceptIndex) {
  return (linhas || []).reduce(
    (ids, line, index) =>
      index === exceptIndex ? ids : ids.concat(lineVehicles(line)),
    [],
  );
}

export function hasAvailableVehicle(linhas, vehicles) {
  const options = vehicles || [];
  if (!options.length) return true;

  const taken = selectedVehicleIds(linhas, -1);
  return options.some((option) => taken.indexOf(Number(option.value)) === -1);
}

export function expandNewValidityLines(block) {
  return ((block && block.linhas) || []).reduce(
    (items, line, lineIndex) =>
      items.concat(
        lineVehicles(line).map((tpRodado) => ({
          lineIndex,
          tpRodado,
          vlr: Number(line.vlr),
        })),
      ),
    [],
  );
}

function mapRangeToApi(faixa) {
  const excedente = isOn(faixa.excedente) ? 1 : 0;

  return {
    vlr_ini: Number(faixa.vlr_ini),
    vlr_fim: Number(faixa.vlr_fim),
    vlr: Number(faixa.vlr),
    fixa: isOn(faixa.fixa) ? 1 : 0,
    excedente,
    vlr_exd: excedente ? Number(faixa.vlr_exd) : 0,
    fator_conv: isBlank(faixa.fator_conv) ? 1 : Number(faixa.fator_conv),
  };
}

function mapCommissionToApi(comissao) {
  const value = comissao || {};
  const payload = {
    vlr_taxa: isBlank(value.vlrTaxa) ? 0 : Number(value.vlrTaxa),
    taxa_comissao: optionalNumber(value.taxaComissao),
    vlr_minimo: optionalNumber(value.vlrMinimo),
  };

  if (!isBlank(value.custoId)) payload.custo = { value: Number(value.custoId) };

  return payload;
}

function mapBlockToApi(block, tpCalc) {
  const payload = {
    dt_vigencia_ini: block.inicio,
    dt_vigencia_fim: block.fim || null,
  };
  const mode = newValidityModeFor(tpCalc);

  if (mode === NEW_VALIDITY_MODE.VEHICLES) {
    payload.veiculos = expandNewValidityLines(block).map((item) => ({
      tp_rodado: { value: item.tpRodado },
      vlr: item.vlr,
    }));
  } else if (mode === NEW_VALIDITY_MODE.RANGES) {
    payload.calculo = (block.faixas || []).map(mapRangeToApi);
  } else if (mode === NEW_VALIDITY_MODE.VALUE) {
    payload.vlr = Number(block.vlr);
  } else if (mode === NEW_VALIDITY_MODE.COMMISSION) {
    payload.comissao = mapCommissionToApi(block.comissao);
  }

  const taxas = block.taxas || createEmptyTaxas();
  payload.taxas = mapTaxasToApi(taxas);
  payload.custo_extra = mapCustosExtraToApi(taxas.custo_extra).map((custo) => ({
    tp_trecho: custo.tp_trecho,
    tp_trecho_custo: custo.tp_trecho_custo,
    custo: custo.custo,
    vlr_taxa: custo.vlr_taxa,
    vlr_minimo: custo.vlr_minimo,
    taxa_comissao: custo.taxa_comissao,
  }));

  return payload;
}

export function mapNewValidityToApi(form, tpCalc) {
  const blocos = (form && form.blocos) || [];
  const payload =
    blocos.length === 1
      ? mapBlockToApi(blocos[0], tpCalc)
      : { vigencias: blocos.map((block) => mapBlockToApi(block, tpCalc)) };

  if (form && !isBlank(form.rotaStatus)) {
    payload.rota_status = Number(form.rotaStatus);
  }

  return payload;
}

function vehicleLabel(vehicles, id) {
  const match = (vehicles || []).filter(
    (option) => Number(option.value) === Number(id),
  )[0];
  return match ? match.label : String(id);
}

function appendMessage(current, message) {
  if (!current) return message;
  return current.split("\n").indexOf(message) === -1
    ? `${current}\n${message}`
    : current;
}

function firstMessage(value) {
  if (Array.isArray(value)) return value.length ? String(value[0]) : "";
  return value ? String(value) : "";
}

export function emptyNewValidityBlockErrors() {
  return {
    periodo: [],
    linhas: {},
    faixas: {},
    valor: "",
    comissao: {},
    taxas: {},
    custos: {},
    campo: [],
  };
}

export function emptyNewValidityErrors() {
  return { geral: [], rotaStatus: "", blocos: {} };
}

function setNested(target, index, field, message) {
  const current = target[index] || {};
  target[index] = { ...current, [field]: appendMessage(current[field], message) };
}

function assignBlockError(blockErrors, block, key, message, vehicles) {
  if (PERIOD_KEYS.indexOf(key) !== -1) {
    if (blockErrors.periodo.indexOf(message) === -1) blockErrors.periodo.push(message);
    return true;
  }

  const rowMatch = ROW_ERROR_KEY.exec(key);
  if (rowMatch) {
    const item = expandNewValidityLines(block)[Number(rowMatch[1])];
    if (!item) return false;

    const field = rowMatch[2] === "vlr" ? "valor" : "veiculo";
    const line = block.linhas[item.lineIndex];
    const text =
      field === "veiculo" && lineVehicles(line).length > 1
        ? `${vehicleLabel(vehicles, item.tpRodado)}: ${message}`
        : message;
    setNested(blockErrors.linhas, item.lineIndex, field, text);
    return true;
  }

  const rangeMatch = RANGE_ERROR_KEY.exec(key);
  if (rangeMatch) {
    const index = Number(rangeMatch[1]);
    if (!(block.faixas || [])[index]) return false;
    setNested(blockErrors.faixas, index, rangeMatch[2], message);
    return true;
  }

  const commissionMatch = COMMISSION_ERROR_KEY.exec(key);
  if (commissionMatch) {
    const field = {
      vlr_taxa: "vlrTaxa",
      taxa_comissao: "taxaComissao",
      vlr_minimo: "vlrMinimo",
      "custo.value": "custoId",
      custo: "custoId",
    }[commissionMatch[1]];
    blockErrors.comissao[field] = appendMessage(blockErrors.comissao[field], message);
    return true;
  }

  const taxMatch = TAX_ERROR_KEY.exec(key);
  if (taxMatch) {
    blockErrors.taxas[taxMatch[1]] = appendMessage(
      blockErrors.taxas[taxMatch[1]],
      message,
    );
    return true;
  }

  const costMatch = COST_ERROR_KEY.exec(key);
  if (costMatch) {
    const custos = (block.taxas && block.taxas.custo_extra) || [];
    const rowIndex = sentCustoExtraIndexes(custos)[Number(costMatch[1])];
    if (rowIndex === undefined) return false;

    const field = costMatch[2] ? COST_FIELD_BY_KEY[costMatch[2]] : "custo_id";
    if (!field) return false;
    setNested(blockErrors.custos, rowIndex, field, message);
    return true;
  }

  if (key === "vlr") {
    blockErrors.valor = appendMessage(blockErrors.valor, message);
    return true;
  }

  if (["veiculos", "calculo", "comissao", "taxas", "custo_extra"].indexOf(key) !== -1) {
    if (blockErrors.campo.indexOf(message) === -1) blockErrors.campo.push(message);
    return true;
  }

  return false;
}

export function mapNewValidityApiErrors(errors, form, vehicles) {
  const result = emptyNewValidityErrors();
  if (!errors || typeof errors !== "object") return result;

  const blocos = (form && form.blocos) || [];
  const soleBlockIndex = blocos.length === 1 ? 0 : -1;

  Object.keys(errors).forEach((key) => {
    const message = firstMessage(errors[key]);
    if (!message) return;

    if (key === ROUTE_STATUS_ERROR_KEY) {
      result.rotaStatus = appendMessage(result.rotaStatus, message);
      return;
    }

    const blockMatch = BLOCK_ERROR_KEY.exec(key);
    const blockIndex = blockMatch ? Number(blockMatch[1]) : soleBlockIndex;
    const fieldKey = blockMatch ? blockMatch[2] : key;
    const block = blocos[blockIndex];

    if (block) {
      const blockErrors = result.blocos[blockIndex] || emptyNewValidityBlockErrors();
      if (assignBlockError(blockErrors, block, fieldKey, message, vehicles)) {
        result.blocos[blockIndex] = blockErrors;
        return;
      }
    }

    result.geral.push(message);
  });

  return result;
}

export function hasNewValidityApiErrors(mapped) {
  if (!mapped) return false;
  if (mapped.geral.length || mapped.rotaStatus) return true;

  return Object.keys(mapped.blocos).some((index) => {
    const block = mapped.blocos[index];
    return (
      block.periodo.length ||
      block.campo.length ||
      block.valor ||
      Object.keys(block.linhas).length ||
      Object.keys(block.faixas).length ||
      Object.keys(block.comissao).length ||
      Object.keys(block.taxas).length ||
      Object.keys(block.custos).length
    );
  });
}

function scalarOf(raw, key) {
  const value = raw ? raw[key] : null;
  if (value && typeof value === "object") {
    return value.valor === undefined ? null : Number(value.valor);
  }
  return isBlank(value) ? null : Number(value);
}

function listOf(raw, ...keys) {
  for (let i = 0; i < keys.length; i += 1) {
    const value = raw ? raw[keys[i]] : null;
    if (Array.isArray(value)) return value;
  }
  return [];
}

function referenceValidity(rawRoute) {
  return listOf(rawRoute, "TabelaFreteRotaVig", "tabela_frete_rota_vig")
    .filter((vigencia) => scalarOf(vigencia, "status") !== STATUS_CANCELADA)
    .sort((a, b) => {
      const byStart = String(b.dt_vigencia_ini || "").localeCompare(
        String(a.dt_vigencia_ini || ""),
      );
      return byStart || Number(b.tabfrerotvig_id) - Number(a.tabfrerotvig_id);
    })[0] || null;
}

export function mapNewValidityContextFromRoute(rawRoute) {
  const reference = referenceValidity(rawRoute);
  const commissionCost =
    listOf(reference, "TabelaFreteRotaVigCusExt", "tabela_frete_rota_vig_cus_ext")
      .filter(
        (custo) =>
          scalarOf(custo, "status") === STATUS_ATIVO &&
          scalarOf(custo, "tp_trecho") === TRECHO_COMISSAO,
      )
      .sort((a, b) => Number(a.tafrvce_id) - Number(b.tafrvce_id))[0] || null;

  const hasActiveVehicle = listOf(
    rawRoute,
    "TabelaFreteRotaMeioTransp",
    "tabela_frete_rota_meio_transp",
  ).some(
    (transp) =>
      scalarOf(transp, "status") === STATUS_ATIVO && !isBlank(transp.partiprod_id),
  );

  return {
    hasActiveVehicle,
    commissionDefaults: commissionCost
      ? {
          vlrTaxa: optionalNumber(commissionCost.vlr_taxa),
          taxaComissao: optionalNumber(commissionCost.taxa_comissao),
          vlrMinimo: optionalNumber(commissionCost.vlr_minimo),
          custoId: optionalNumber(commissionCost.custo_id),
        }
      : null,
    // Nova vigência abre as abas de taxa vazias; a comissão continua na referência.
    taxDefaults: null,
  };
}

export function formatIsoDateBr(value) {
  if (!value) return "";
  const parts = String(value).split(" ")[0].split("-");
  if (parts.length !== 3) return String(value);
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

export function describeClosedValidity(response) {
  const closed = response && response.vigencia_fechada;
  if (!closed || !closed.dt_vigencia_fim) return "";

  return `A vigência anterior foi encerrada em ${formatIsoDateBr(
    closed.dt_vigencia_fim,
  )}.`;
}
